/**
 * Refund / Return Policy — required for Stripe + consumer protection.
 * Plain-English, generous, easy to invoke. 30-day window.
 */
export function RefundPolicyPage({ onClose }) {
  return (
    <div className="legal-page" data-testid="refund-page">
      <div className="legal-container">
        <button className="legal-back-btn" onClick={onClose} data-testid="refund-back-btn">← Back</button>
        <h1>Refund Policy</h1>
        <p className="legal-meta">Last updated: February 2026</p>

        <p className="legal-intro">
          We want you to actually like Aura. If you don't, we'll refund you — no interrogation, no guilt trip.
        </p>

        <h2>1. The 30-day window</h2>
        <p>
          You can request a full refund on any Aura purchase within <strong>30 days of the charge date</strong>.
          That covers:
        </p>
        <ul>
          <li>Your one-time <strong>Base ($4.99)</strong> purchase.</li>
          <li>Your first month of any subscription tier (<strong>Spark, Efflora, Aurora, Eternal</strong>).</li>
          <li>Any single renewal where you forgot to cancel — we'll honor it inside the 30-day window.</li>
        </ul>

        <h2>2. How to request a refund</h2>
        <p>
          Email <a href="mailto:support@wwwwhynot.com">support@wwwwhynot.com</a> from the address on your account.
          Include:
        </p>
        <ul>
          <li>The charge date (or last 4 digits of the card used)</li>
          <li>A one-line reason (optional, but it helps us improve)</li>
        </ul>
        <p>
          We process refunds within <strong>5 business days</strong>. The money lands back on your original
          payment method within another 5–10 days, depending on your bank.
        </p>

        <h2>3. What happens to your account</h2>
        <ul>
          <li>If you refund a <strong>monthly subscription</strong>, your premium access ends immediately on refund.</li>
          <li>If you refund the <strong>Base</strong> lifetime purchase, your account reverts to trial-expired status. You can re-purchase later if you change your mind.</li>
          <li>Your chats, memories, and wallpapers stay safe — refunding the plan doesn't delete your data.
            To delete data, use <em>Settings → Delete my account</em>.</li>
        </ul>

        <h2>4. After 30 days</h2>
        <p>
          Outside the 30-day window, we generally don't issue refunds — but we will <em>always</em> consider
          exceptions for billing errors, double charges, or genuine fraud. Just email us. We're humans.
        </p>

        <h2>5. Cancellations vs. refunds</h2>
        <p>
          You can cancel a subscription anytime in <em>Settings → Manage subscription</em>. Cancelling stops
          future charges but doesn't automatically refund the current billing period. If you want both
          (cancel + refund the most recent charge), say so in your email and we'll do both.
        </p>

        <h2>6. Promo codes & gifted subscriptions</h2>
        <p>
          Refunds for promo-discounted purchases refund only the amount actually paid. Gifted subscriptions
          (e.g. Eternal's "one free month to a friend") are non-refundable to the gifter, but the recipient
          can decline before activating.
        </p>

        <h2>7. Chargebacks</h2>
        <p>
          Please email us before filing a chargeback with your bank — we'd much rather just give you your
          money back directly. Filing a chargeback closes the account it's tied to (we have to dispute it
          via Stripe), so the email route is faster and friendlier for everyone.
        </p>

        <h2>8. Contact</h2>
        <p>
          <a href="mailto:support@wwwwhynot.com">support@wwwwhynot.com</a> — we usually reply within
          24 hours, often the same day.
        </p>
      </div>
    </div>
  );
}
